/**
 * `useOrgIdentities(orgId)` / `useCohortIdentities(cohortId)` — **fuente única**
 * de la identidad de las personas en el cliente (D3, plan 06 · etapas E2/E3).
 *
 * Antes había seis joins de identidad repartidos por las pantallas de gente,
 * horario y cohortes, todos apoyados en la tabla `"user"` que los streams
 * `organizations`, `my_cohorts_teacher` y `org_admin_scope` replicaban —con el
 * correo de todo el mundo— a cualquier miembro. El problema NO era el
 * transporte: el REST que la sustituye filtraba exactamente igual de mal hasta
 * E2. Ahora el servidor redacta el correo salvo para quien tiene `ManageMembers`
 * o `ViewCampusData` (`org_rbac::can_view_member_emails`), y estos hooks son el
 * único punto por el que la identidad entra al cliente.
 *
 * **Camino único desde el commit restrictivo de E3.** El flag
 * `academy.data.rest_identities` conmutaba entre REST y el espejo local; con
 * `"user"` fuera de los streams, la tabla local tiene UNA fila (la propia) y no
 * hay a dónde volver, así que la rama de rollback y el flag desaparecen. El
 * permiso nunca colgó del flag: apagarlo no reabría la fuga en el servidor.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  academyPeopleQueryKeys,
  cohortQueryKeys,
  type components,
  type OrgIdentity,
} from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

type MemberDTO = components["schemas"]["OrganizationMemberDTO"];
type RosterEntryDTO = components["schemas"]["CohortRosterEntryDTO"];

/** Mapa `userId → identidad` + señal de carga (lo que consumen las pantallas). */
export interface OrgIdentitiesResult {
  identities: Accessor<ReadonlyMap<string, OrgIdentity>>;
  isLoading: Accessor<boolean>;
  /** Identidad de una persona concreta (`undefined` mientras no haya llegado). */
  identityOf: (userId: string | undefined) => OrgIdentity | undefined;
}

/** Normaliza la respuesta REST de `listMembers` a la forma compartida. */
function fromMemberDTO(m: MemberDTO): OrgIdentity {
  return {
    userId: m.userId,
    role: m.role,
    campusId: m.campusId ?? null,
    name: m.user.name || null,
    // `null` = redactado por capacidad; la interfaz lo trata como "sin correo".
    email: m.user.email ?? null,
    image: m.user.image ?? null,
  };
}

/** Normaliza una entrada del roster de cohorte a la forma compartida. */
function fromRosterDTO(e: RosterEntryDTO): OrgIdentity {
  return {
    userId: e.userId,
    role: e.role ?? null,
    campusId: e.campusId ?? null,
    name: e.name ?? null,
    // `null` = redactado por capacidad, igual que en `listMembers`.
    email: e.email ?? null,
    image: e.image ?? null,
  };
}

/** Empaqueta una lista de DTOs en el resultado que consumen las pantallas. */
function toResult<T>(
  rows: Accessor<T[] | undefined>,
  isLoading: Accessor<boolean>,
  map: (row: T) => OrgIdentity,
): OrgIdentitiesResult {
  const identities = createMemo<ReadonlyMap<string, OrgIdentity>>(() => {
    const out = new Map<string, OrgIdentity>();
    for (const row of rows() ?? []) {
      const identity = map(row);
      out.set(identity.userId, identity);
    }
    return out;
  });
  return {
    identities,
    isLoading,
    identityOf: (userId) => (userId ? identities().get(userId) : undefined),
  };
}

export function useOrgIdentities(orgId: Accessor<string | undefined>): OrgIdentitiesResult {
  const rest = createApiQuery<MemberDTO[], unknown>(
    () => academyPeopleQueryKeys.identities(orgId()),
    async () =>
      api.GET("/api/org/listMembers", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    }),
  );
  return toResult(
    () => rest.data,
    () => rest.isLoading,
    fromMemberDTO,
  );
}

/**
 * Identidad de la gente de UNA COHORTE (alumnos matriculados + profesores
 * asignados) — plan 06 · E3.
 *
 * Es el mismo hook que [`useOrgIdentities`] con otro alcance, y devuelve el
 * mismo `Map<userId, OrgIdentity>`: el roster, el informe de asistencia y las
 * alertas hidratan todos por aquí, y ninguna pantalla vuelve a unir con
 * `"user"` por su cuenta.
 *
 * Por qué existe además del org-scoped: `GET /api/cohorts/:id/roster` autoriza
 * por cohorte (profesor de ESA cohorte, campus_admin de SU sede, org_admin) y
 * devuelve 20 personas donde `listMembers` devolvería las 2 000 de la academia.
 * Usar el endpoint org-wide para pintar un roster sería pedir de más y
 * autorizar por lo más ancho.
 */
export function useCohortIdentities(cohortId: Accessor<string | undefined>): OrgIdentitiesResult {
  const rest = createApiQuery<RosterEntryDTO[], unknown>(
    () => cohortQueryKeys.identities(cohortId()),
    async () =>
      api.GET("/api/cohorts/{id}/roster", {
        params: { path: { id: cohortId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(cohortId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    }),
  );
  return toResult(
    () => rest.data,
    () => rest.isLoading,
    fromRosterDTO,
  );
}
